import React from 'react'
import { useTranslation } from 'react-i18next'
import { XIcon, BellIcon } from './Icons'

interface Announcement {
  id: number
  title: string
  content: string
  type: 'feature' | 'improvement' | 'bug_fix' | 'maintenance'
  created_at: string
  created_by: string
}

interface AnnouncementModalProps {
  announcement: Announcement
  onClose: () => void
}

const AnnouncementModal: React.FC<AnnouncementModalProps> = ({ announcement, onClose }) => {
  const { t } = useTranslation()

  const getTypeColor = (type: string) => {
    switch (type) {
      case 'feature':
        return 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300'
      case 'improvement':
        return 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300'
      case 'bug_fix':
        return 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300'
      case 'maintenance':
        return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300'
      default:
        return 'bg-gray-100 text-gray-800 dark:bg-gray-900/30 dark:text-gray-300'
    }
  }

  const getTypeIcon = (type: string) => {
    switch (type) {
      case 'feature':
        return '✨'
      case 'improvement':
        return '🚀'
      case 'bug_fix':
        return '🐛'
      case 'maintenance':
        return '🔧'
      default:
        return '📢'
    }
  }

  const getTypeLabel = (type: string) => {
    switch (type) {
      case 'feature':
        return t('dashboard.announcements.types.feature')
      case 'improvement':
        return t('dashboard.announcements.types.improvement')
      case 'bug_fix':
        return t('dashboard.announcements.types.bug_fix')
      case 'maintenance':
        return t('dashboard.announcements.types.maintenance')
      default:
        return type
    }
  }

  return (
    <>
      {/* Backdrop */}
      <div 
        className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 animate-in fade-in duration-200"
        onClick={onClose}
      />
      
      {/* Modal */}
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4 pointer-events-none">
        <div 
          className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-hidden pointer-events-auto animate-in zoom-in-95 duration-200"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className="relative bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 dark:from-gray-950 dark:via-gray-900 dark:to-gray-950 p-8">
            {/* Close Button */}
            <button
              onClick={onClose}
              className="absolute top-4 right-4 p-2 rounded-lg bg-white/10 hover:bg-white/20 text-white transition-colors"
              aria-label="Close"
            >
              <XIcon className="h-5 w-5" />
            </button>

            {/* Icon */}
            <div className="flex items-center justify-center mb-4">
              <div className="p-4 bg-white/10 rounded-2xl backdrop-blur-sm">
                <BellIcon className="h-8 w-8 text-white" />
              </div>
            </div>

            {/* Header Text */}
            <div className="text-center">
              <p className="text-white/80 text-sm font-medium mb-2 uppercase tracking-wider">
                {t('dashboard.announcements.newAnnouncement')}
              </p>
              <h2 className="text-3xl font-bold text-white mb-3 leading-tight">
                {announcement.title}
              </h2>
              
              {/* Type Badge */}
              <div className={`inline-flex items-center gap-2 px-4 py-1.5 rounded-full text-sm font-semibold ${getTypeColor(announcement.type)}`}>
                <span>{getTypeIcon(announcement.type)}</span>
                {getTypeLabel(announcement.type)}
              </div>
            </div>
          </div>

          {/* Content */}
          <div className="p-8 overflow-y-auto max-h-[50vh]">
            <div className="prose prose-gray dark:prose-invert max-w-none">
              <p className="text-gray-700 dark:text-gray-300 leading-relaxed whitespace-pre-wrap">
                {announcement.content}
              </p>
            </div>

            {/* Meta Info */}
            <div className="mt-6 pt-6 border-t border-gray-200 dark:border-gray-700">
              <div className="flex items-center justify-between text-sm text-gray-600 dark:text-gray-400">
                <div className="flex items-center gap-2">
                  <span className="font-medium">{announcement.created_by}</span>
                </div>
                <time dateTime={announcement.created_at}>
                  {new Date(announcement.created_at).toLocaleDateString(undefined, {
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric'
                  })}
                </time>
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="p-6 bg-gray-50 dark:bg-gray-900 border-t border-gray-200 dark:border-gray-700">
            <button
              onClick={onClose}
              className="w-full px-6 py-3 bg-gray-900 dark:bg-gray-100 text-white dark:text-gray-900 rounded-lg font-semibold hover:bg-gray-800 dark:hover:bg-gray-200 transition-colors shadow-lg"
            >
              {t('common.gotIt')}
            </button>
          </div>
        </div>
      </div>
    </>
  )
}

export default AnnouncementModal

